import React from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import { formatElement } from 'views/Pages/PagesForm/utils';
import { z } from 'zod';

import { cn } from 'components/lib/utils';
import Textarea from 'components/Textarea/Textarea';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../components/ui/form';
import { IBlockContentProps } from '../../../constants';
import { htmlBlockSchema, pageFormSchema } from '../../../validation-schema';
import { BlockSegmentsField } from '../Fields/BlockSegmentsField';
import { BlockVisibilityField } from '../Fields/BlockVisibilityField';

const formatHtml = (html: string): string => {
  if (!html || !html.trim()) return html;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body;

    const formatted = formatElement(body, 0).trim();
    return formatted || html;
  } catch (error) {
    console.warn('HTML formatting failed:', error);
    return html;
  }
};

type HtmlBlockType = z.infer<typeof htmlBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

// Helper type for contents preview block field render props
type HtmlFieldProps<T extends keyof HtmlBlockType> = ControllerRenderProps<
  PageFormType,
  any
> & {
  value: HtmlBlockType[T];
};

export const HtmlForm: React.FC<IBlockContentProps> = ({
  block,
  blockIndex
}) => {
  const { control, trigger } = useFormContext<z.infer<typeof pageFormSchema>>();

  const basePath = `blocksV2.${blockIndex}` as const;

  const handleBlur = (
    e: React.FocusEvent<HTMLTextAreaElement>,
    field: HtmlFieldProps<'html'>
  ) => {
    const value = e.target.value;
    if (value) {
      // Parse HTML to validate structure
      const doc = new DOMParser().parseFromString(value, 'text/html');

      // Check for parsing errors (DOMParser adds error elements for invalid HTML)
      const parserErrors = doc.querySelector('parsererror');
      if (parserErrors) {
        // HTML is malformed, but we'll still sanitize what we can
        console.warn('HTML parsing detected errors:', parserErrors.textContent);
      }

      // Format HTML for better display
      const formatted = formatHtml(value);

      // Update field with formatted and sanitized value
      field.onChange(formatted === '' ? undefined : formatted);
    }
    trigger(`${basePath}.html`);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    onChange: (value: string | undefined) => void
  ) => {
    const value = e.target.value;
    onChange(value === '' ? undefined : value);
    clearTimeout((window as any).__blocksV2DebounceTimeout);
    (window as any).__blocksV2DebounceTimeout = setTimeout(() => {
      const event = new CustomEvent('blocksV2:update');
      window.dispatchEvent(event);
    }, 500);
  };

  return (
    <div
      className={
        'flex flex-col gap-4 p-4 rounded-md border transition-colors border-base-border'
      }
    >
      <FormField
        control={control}
        name={`${basePath}.html`}
        render={({ field }: { field: HtmlFieldProps<'html'> }) => (
          <FormItem>
            <FormLabel>* HTML</FormLabel>
            <FormControl>
              <Textarea
                className={cn(
                  'bg-stone-800 text-base-mutedForeground font-mono'
                )}
                placeholder="Edit your HTML code here"
                {...field}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleChange(e, field.onChange)
                }
                value={field.value ?? ''}
                rows={8}
                onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                  handleBlur(e, field)
                }
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s" />
          </FormItem>
        )}
      />
      <FormMessage />
      <BlockVisibilityField basePath={basePath} />
      <BlockSegmentsField basePath={basePath} />
    </div>
  );
};
